import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiSearch,
  FiX,
  FiUser,
  FiTrendingUp,
  FiActivity,
  FiUsers,
  FiClock,
  FiShield,
} from "react-icons/fi";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  getWebTaskSummary,
  getWebTaskSessions,
  getWebTaskUserDetail,
  getWebTasks,
} from "../api/webTaskAdminApi";

const LIMIT = 50;

/** §6 status values + color scheme */
const STATUS_META = {
  COMPLETED: { color: "text-green-600", chip: "bg-green-50 text-green-700 border-green-200", bar: "bg-green-500", note: "Valid, EP credited" },
  TIMEOUT: { color: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-400", note: "Client timed out or ran past 150s guard" },
  INSUFFICIENT_PROGRESS: { color: "text-gray-600", chip: "bg-gray-100 text-gray-700 border-gray-200", bar: "bg-gray-400", note: "Exited before minimum tier" },
  BOT_CLICKS: { color: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500", note: "Tap rate > 2.5 clicks/sec" },
  CHEAT_SLEEP: { color: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500", note: "cheatDetected or sleep > 25s" },
  TIME_SPOOFED: { color: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500", note: "activeSeconds exceeds wall-clock + 3s" },
  STARTED: { color: "text-blue-600", chip: "bg-blue-50 text-blue-700 border-blue-200", bar: "bg-blue-400", note: "Session open" },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

const fmt = (n) =>
  n == null || isNaN(Number(n)) ? "0" : Number(n).toLocaleString();

const daysAgoUtc = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

/* ---------------- Per-user detail modal ---------------- */
const UserDetailModal = ({ userId, range, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await getWebTaskUserDetail(userId, range);
        const data = res?.data || res || {};
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Failed to load user web task data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessions = detail?.sessions || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-xs" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-4 border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
              <FiUser size={18} />
              {detail?.displayName || detail?.name || detail?.username || detail?.user?.displayName || detail?.user?.username || (detail?.email ? detail.email.split("@")[0] : null) || `User #${userId}`}
            </h3>
            <p className="text-xs text-gray-400">
              {detail?.email || "Web task history"} · {fmt(detail?.totalSessions)} sessions ·{" "}
              {fmt(detail?.totalEp)} total EP
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-black rounded-lg hover:bg-gray-100 transition cursor-pointer"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Current event standing */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Total Sessions</div>
                  <div className="text-xl font-black text-gray-900 font-mono">{fmt(detail?.totalSessions)}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Total EP</div>
                  <div className="text-xl font-black text-gray-900 font-mono">{fmt(detail?.totalEp)}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Current Event Points</div>
                  <div className="text-xl font-black text-green-700 font-mono">{fmt(detail?.currentEventPoints)}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Current Event Rank</div>
                  <div className="text-xl font-black text-gray-900 font-mono">
                    {detail?.currentEventRank ? `#${detail.currentEventRank}` : "—"}
                  </div>
                </div>
              </div>

              {/* Session rows */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                  Session History
                </h4>
                {sessions.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">
                    No web task sessions found for this user in the selected range.
                  </p>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                          <th className="px-3 py-2.5">Task</th>
                          <th className="px-3 py-2.5">Status</th>
                          <th className="px-3 py-2.5">EP</th>
                          <th className="px-3 py-2.5">Active</th>
                          <th className="px-3 py-2.5">Clicks / Pages</th>
                          <th className="px-3 py-2.5">Tier</th>
                          <th className="px-3 py-2.5">Completed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sessions.map((s, i) => {
                          const meta = STATUS_META[s.status] || STATUS_META.COMPLETED;
                          return (
                            <tr key={s.id || i} className="hover:bg-gray-50">
                              <td className="px-3 py-2.5 font-semibold text-gray-800">
                                {s.taskTitle || s.taskId || "—"}
                              </td>
                              <td className="px-3 py-2.5">
                                <span
                                  className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.chip}`}
                                >
                                  {s.status}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 font-mono font-bold text-green-700">
                                {Number(s.earnedEP) > 0 ? `+${fmt(s.earnedEP)}` : "0"}
                              </td>
                              <td className="px-3 py-2.5 text-gray-500 font-mono">{fmt(s.activeSeconds)}s</td>
                              <td className="px-3 py-2.5 text-gray-500 font-mono">
                                {fmt(s.clicks)} / {fmt(s.pages)}
                              </td>
                              <td className="px-3 py-2.5 text-gray-500">{s.matchedTierLabel || "—"}</td>
                              <td className="px-3 py-2.5 text-gray-500">{formatDateTime(s.completedAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Main Audit Page ---------------- */
export default function WebTaskAudit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTaskId = searchParams.get("taskId") || "";

  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");
  const [apply7d, setApply7d] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tasks, setTasks] = useState([]);

  const [statusFilter, setStatusFilter] = useState(initialTaskId ? "" : "");
  const [taskId, setTaskId] = useState(initialTaskId);
  const [search, setSearch] = useState("");
  const [includeOpen, setIncludeOpen] = useState(false); // §4.8: include in-flight sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [detailUser, setDetailUser] = useState(null);

  const rangeForApi = () => ({
    from: apply7d ? daysAgoUtc(7) : from || undefined,
    to: apply7d ? daysAgoUtc(0) : to || undefined,
  });

  // Load task dropdown for the filter
  useEffect(() => {
    (async () => {
      try {
        const res = await getWebTasks({ page: 1, limit: 200 });
        setTasks(res?.data?.webTasks || []);
      } catch {
        // non-fatal — the sessions table still works with plain task IDs
      }
    })();
  }, []);

  // §4.6 global summary
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const res = await getWebTaskSummary(rangeForApi());
        if (!cancelled) setSummary(res?.data || res || null);
      } catch (err) {
        console.error("Failed to load summary:", err);
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, apply7d]);

  // §4.7 sessions list
  const loadSessions = useCallback(
    async (silent = false) => {
      if (!silent) setSessionsLoading(true);
      try {
        const res = await getWebTaskSessions({
          page: currentPage,
          limit: LIMIT,
          taskId: taskId || undefined,
          status: statusFilter || undefined,
          search: search || undefined,
          includeOpen,
          ...(rangeForApi()),
        });
        const data = res?.data || res || {};
        setSessions(data.sessions || []);
        const pg = data.pagination || {};
        setTotalItems(Number(pg.totalItems || 0));
        setTotalPages(Number(pg.totalPages || 1));
      } catch (err) {
        console.error("Failed to load sessions:", err);
        setSessions([]);
        if (!silent) toast.error(err.message || "Failed to load sessions");
      } finally {
        if (!silent) setSessionsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, taskId, statusFilter, search, includeOpen, from, to, apply7d]
  );

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const applyFilters = () => {
    setCurrentPage(1);
    const params = new URLSearchParams();
    if (apply7d) {
      params.set("from", daysAgoUtc(7));
      params.set("to", daysAgoUtc(0));
    } else {
      if (from) params.set("from", from);
      if (to) params.set("to", to);
    }
    if (taskId) params.set("taskId", taskId);
    if (statusFilter) params.set("status", statusFilter);
    setSearchParams(params, { replace: true });
    loadSessions();
  };

  const byStatus = Array.isArray(summary?.byStatus) ? summary.byStatus : [];
  const totals = summary?.totals || {};
  const maxStatusCount = Math.max(1, ...byStatus.map((s) => Number(s.count) || 0));
  const topUsers = Array.isArray(summary?.topUsers) ? summary.topUsers : [];
  const fraudCount = byStatus
    .filter((s) => ["BOT_CLICKS", "CHEAT_SLEEP", "TIME_SPOOFED"].includes(s.status))
    .reduce((acc, s) => acc + (Number(s.count) || 0), 0);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-6 sm:p-8 space-y-6 w-full">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium flex-wrap">
            <Link to="/web-tasks" className="hover:text-black hover:underline transition">
              Web Tasks
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
              Session Audit
            </span>
          </div>

          {/* Header + range */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                Web Task Sessions
                {taskId && (
                  <span className="text-xs font-bold bg-white border border-gray-300 rounded-lg px-2.5 py-1 text-gray-600 font-mono">
                    {taskId}
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Anti-fraud review of partner web task sessions · EP settles into the running event
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs font-bold text-gray-400 uppercase">Range</label>
              <button
                onClick={() => {
                  setApply7d(true);
                  setFrom("");
                  setTo("");
                }}
                className={`text-xs font-semibold px-3 py-2 rounded-xl border transition cursor-pointer ${
                  apply7d
                    ? "bg-black text-[#ffff00] border-black"
                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                Last 7 days
              </button>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setApply7d(false);
                }}
                className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-black"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setApply7d(false);
                }}
                className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-black"
              />
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-black text-[#ffff00] text-xs font-bold rounded-xl hover:bg-gray-800 transition cursor-pointer"
              >
                Apply
              </button>
            </div>
          </div>

          {/* §4.6 Dashboard */}
          <div className="space-y-4">
            {/* Totals cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  icon: <FiActivity size={18} />,
                  label: "Total Sessions",
                  value: fmt(totals.sessions),
                  accent: "text-gray-900",
                  sub: `${fmt(totals.completed)} completed`,
                },
                {
                  icon: <FiTrendingUp size={18} />,
                  label: "Total EP Awarded",
                  value: fmt(totals.totalEpAwarded),
                  accent: "text-green-700",
                  sub: "credited to running event",
                },
                {
                  icon: <FiUsers size={18} />,
                  label: "Unique Users",
                  value: fmt(totals.uniqueUsers),
                  accent: "text-blue-700",
                  sub: "in range",
                },
                {
                  icon: <FiShield size={18} />,
                  label: "Fraud-Flagged",
                  value: summaryLoading ? "…" : fmt(fraudCount),
                  accent: fraudCount > 0 ? "text-red-600" : "text-gray-900",
                  sub: "bot / cheat / spoofed",
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex items-start gap-3"
                >
                  <div className="p-2.5 bg-gray-50 rounded-xl text-gray-500">{card.icon}</div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                      {card.label}
                    </div>
                    <div className={`text-2xl font-black font-mono ${card.accent}`}>{card.value}</div>
                    <div className="text-[11px] text-gray-400">{card.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* byStatus bars */}
            {byStatus.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                  Sessions by Close Reason
                </h3>
                <div className="space-y-2">
                  {byStatus.map((row) => {
                    const meta = STATUS_META[row.status] || STATUS_META.COMPLETED;
                    const pct = Math.max(2, Math.round((Number(row.count) / maxStatusCount) * 100));
                    return (
                      <button
                        key={row.status}
                        onClick={() => {
                          setStatusFilter(row.status);
                          setCurrentPage(1);
                        }}
                        className="w-full flex items-center gap-3 group cursor-pointer"
                        title={meta.note}
                      >
                        <span className="w-44 text-left text-[11px] font-mono font-bold text-gray-600 group-hover:text-black shrink-0">
                          {row.status}
                        </span>
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${meta.bar} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-16 text-right text-xs font-mono font-bold text-gray-700 shrink-0">
                          {fmt(row.count)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  Red bars are fraud signals — BOT_CLICKS, CHEAT_SLEEP, TIME_SPOOFED.
                  Click a row to filter the sessions table below.
                </p>
              </div>
            )}

            {/* top users */}
            {topUsers.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2">
                  <FiClock size={13} /> Top Web Task Users (in range)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {topUsers.map((u) => (
                    <button
                      key={u.userId}
                      onClick={() => {
                        setSearch(String(u.username || u.userId));
                        setStatusFilter("");
                        setDetailUser(u.userId);
                      }}
                      className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 hover:border-black transition cursor-pointer text-left"
                    >
                      <FiUser size={14} className="text-gray-400" />
                      <div>
                        <div className="text-xs font-bold text-gray-800">
                          {u.username} <span className="text-gray-400 font-mono">#{u.userId}</span>
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {fmt(u.sessions)} sessions · {fmt(u.totalEp)} EP
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* §4.7 Sessions table */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-gray-100 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={taskId}
                  onChange={(e) => {
                    setTaskId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold outline-none focus:border-black cursor-pointer min-w-[180px]"
                >
                  <option value="">All Tasks</option>
                  {tasks.map((t) => (
                    <option key={t.taskId} value={t.taskId}>
                      {t.title || t.taskId}
                    </option>
                  ))}
                  {taskId && !tasks.some((t) => t.taskId === taskId) && (
                    <option value={taskId}>{taskId}</option>
                  )}
                </select>

                <div className="relative flex-1 min-w-[180px] max-w-xs">
                  <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setCurrentPage(1);
                        loadSessions();
                      }
                    }}
                    placeholder="Username / email / user ID…"
                    className="w-full bg-white border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-xs outline-none focus:border-black focus:ring-1 focus:ring-black"
                  />
                </div>

                {statusFilter && (
                  <button
                    onClick={() => {
                      setStatusFilter("");
                      setCurrentPage(1);
                    }}
                    className="flex items-center gap-1.5 text-[11px] font-bold bg-black text-[#ffff00] rounded-full px-3 py-1.5 cursor-pointer"
                  >
                    {statusFilter} <FiX size={12} />
                  </button>
                )}

                <label className="flex items-center gap-2 text-[11px] font-semibold text-gray-500 cursor-pointer select-none ml-auto">
                  <input
                    type="checkbox"
                    checked={includeOpen}
                    onChange={(e) => {
                      setIncludeOpen(e.target.checked);
                      setCurrentPage(1);
                    }}
                    className="h-3.5 w-3.5 accent-black"
                  />
                  Include in-flight sessions (STARTED)
                </label>
              </div>

              {/* Status filter chips */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  "COMPLETED",
                  "TIMEOUT",
                  "INSUFFICIENT_PROGRESS",
                  "BOT_CLICKS",
                  "CHEAT_SLEEP",
                  "TIME_SPOOFED",
                ].map((s) => {
                  const meta = STATUS_META[s];
                  const active = statusFilter === s;
                  return (
                    <button
                      key={s}
                      onClick={() => {
                        setStatusFilter(active ? "" : s);
                        setCurrentPage(1);
                      }}
                      title={meta.note}
                      className={`text-[10px] font-mono font-bold px-2.5 py-1 rounded-full border transition cursor-pointer ${
                        active ? meta.chip : "bg-white border-gray-200 text-gray-500 hover:border-gray-400"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Table */}
            {sessionsLoading ? (
              <div className="flex items-center justify-center p-12">
                <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-xs text-gray-500 uppercase font-bold tracking-wider">
                      <th className="px-4 py-4">User</th>
                      <th className="px-4 py-4">Task</th>
                      <th className="px-4 py-4">Status</th>
                      <th className="px-4 py-4 text-right">EP</th>
                      <th className="px-4 py-4 text-right">Active</th>
                      <th className="px-4 py-4 text-right">Clicks / Pages</th>
                      <th className="px-4 py-4">Tier</th>
                      <th className="px-4 py-4">Completed</th>
                      <th className="px-4 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sessions.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-12 text-center text-gray-500">
                          No sessions match the current filters.
                          <br />
                          <span className="text-xs text-gray-400">
                            Try widening the date range or clearing filters.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      sessions.map((s) => {
                        const meta = STATUS_META[s.status] || STATUS_META.COMPLETED;
                        const isFraud = ["BOT_CLICKS", "CHEAT_SLEEP", "TIME_SPOOFED"].includes(s.status);
                        return (
                          <tr
                            key={s.id || s.sessionId}
                            className={`hover:bg-gray-50 transition-colors ${isFraud ? "bg-red-50/40" : ""}`}
                          >
                            <td className="px-4 py-4">
                              <button
                                onClick={() => setDetailUser(s.userId)}
                                className="text-left group cursor-pointer"
                                title="Open user web task data"
                              >
                                <div className="font-semibold text-gray-900 group-hover:underline">
                                  {s.displayName || s.name || s.username || s.user?.displayName || s.user?.name || s.user?.username || (s.email ? s.email.split("@")[0] : null) || `User #${s.userId}`}
                                </div>
                                <div className="text-[11px] text-gray-400">
                                  {s.email || s.user?.email || `ID #${s.userId}`}
                                </div>
                              </button>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2.5">
                                {s.taskLogo ? (
                                  <img
                                    src={s.taskLogo}
                                    alt=""
                                    className="h-7 w-7 rounded-md object-cover bg-gray-100 border border-gray-200 shrink-0"
                                  />
                                ) : null}
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-800 truncate max-w-[180px]">
                                    {s.taskTitle || s.taskId}
                                  </div>
                                  <div className="text-[11px] font-mono text-gray-400">{s.taskId}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.chip}`}
                                title={meta.note}
                              >
                                {s.status}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-bold">
                              <span className={Number(s.earnedEP) > 0 ? "text-green-700" : "text-gray-400"}>
                                {Number(s.earnedEP) > 0 ? `+${fmt(s.earnedEP)}` : "0"}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right text-gray-500 font-mono text-xs">
                              {fmt(s.activeSeconds)}s
                            </td>
                            <td className="px-4 py-4 text-right text-gray-500 font-mono text-xs">
                              {fmt(s.clicks)} / {fmt(s.pages)}
                            </td>
                            <td className="px-4 py-4 text-xs text-gray-500">{s.matchedTierLabel || "—"}</td>
                            <td className="px-4 py-4 text-xs text-gray-500">
                              {s.status === "STARTED" ? (
                                <span className="text-blue-600 font-semibold">
                                  In progress — started {formatDateTime(s.startTime)}
                                </span>
                              ) : (
                                formatDateTime(s.completedAt)
                              )}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <button
                                onClick={() => setDetailUser(s.userId)}
                                className="text-[11px] font-bold text-gray-500 hover:text-black underline cursor-pointer"
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * LIMIT + 1}–
                  {Math.min(currentPage * LIMIT, totalItems)} of {fmt(totalItems)} sessions
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage <= 1 || sessionsLoading}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-100 transition cursor-pointer"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium text-gray-700">
                    Page {currentPage} of {Math.max(totalPages, 1)}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, Math.max(totalPages, 1)))}
                    disabled={currentPage >= (totalPages || 1) || sessionsLoading}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-100 transition cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {detailUser != null && (
          <UserDetailModal
            userId={detailUser}
            range={rangeForApi()}
            onClose={() => setDetailUser(null)}
          />
        )}
      </div>
    </div>
  );
}
